/**
 * LocationPicker - Interactive map component for selecting location
 * Uses Leaflet/OpenStreetMap for the map display
 */
import React, { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import { useTranslation } from 'react-i18next';
import { MapPin, Navigation, Search, X, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icon in React-Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom orange marker for XPerience
const orangeIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-orange.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// Component to handle map clicks
function MapClickHandler({ onLocationSelect }) {
  useMapEvents({
    click(e) {
      onLocationSelect(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

// Component to recenter map
function MapRecenter({ center }) {
  const map = useMap();
  useEffect(() => {
    if (center) {
      map.setView(center, map.getZoom());
    }
  }, [center, map]);
  return null;
}

const LocationPicker = ({ 
  initialLat, 
  initialLng, 
  initialAddress,
  onLocationChange,
  onClose 
}) => {
  const { i18n } = useTranslation();
  const [position, setPosition] = useState(
    initialLat && initialLng ? [initialLat, initialLng] : null
  );
  const [address, setAddress] = useState(initialAddress || '');
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [gettingLocation, setGettingLocation] = useState(false);
  const [mapCenter, setMapCenter] = useState(
    initialLat && initialLng ? [initialLat, initialLng] : [46.8182, 8.2275] // Switzerland center
  );

  // Reverse geocode to get address from coordinates
  const reverseGeocode = async (lat, lng) => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=14&addressdetails=1`,
        { headers: { 'Accept-Language': i18n.language } }
      );
      const data = await response.json();
      
      if (data.address) {
        const city = data.address.city || data.address.town || data.address.village || data.address.municipality || '';
        const country = data.address.country || '';
        return city && country ? `${city}, ${country}` : data.display_name?.split(',').slice(0, 2).join(',').trim();
      }
      return null;
    } catch (error) {
      console.error('Reverse geocoding failed:', error);
      return null;
    }
  };

  // Search for address
  const searchAddress = async () => {
    if (!searchQuery.trim()) return;
    
    setSearching(true);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchQuery)}&limit=1`,
        { headers: { 'Accept-Language': i18n.language } }
      );
      const data = await response.json();
      
      if (data && data.length > 0) {
        const result = data[0];
        const lat = parseFloat(result.lat);
        const lng = parseFloat(result.lon);
        const newAddress = result.display_name?.split(',').slice(0, 3).join(',').trim();
        
        setPosition([lat, lng]);
        setMapCenter([lat, lng]);
        setAddress(newAddress);
      }
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setSearching(false);
    }
  };

  // Get current location
  const getCurrentLocation = () => {
    if (!navigator.geolocation) return;
    
    setGettingLocation(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        
        setPosition([lat, lng]);
        setMapCenter([lat, lng]);
        
        const newAddress = await reverseGeocode(lat, lng);
        if (newAddress) {
          setAddress(newAddress);
        }
        
        setGettingLocation(false);
      },
      (error) => {
        console.error('Geolocation error:', error);
        setGettingLocation(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  // Handle map click
  const handleMapClick = async (lat, lng) => {
    setPosition([lat, lng]);
    const newAddress = await reverseGeocode(lat, lng);
    if (newAddress) {
      setAddress(newAddress);
    }
  };

  // Confirm selection
  const handleConfirm = () => {
    if (position) {
      onLocationChange({
        latitude: position[0],
        longitude: position[1],
        address: address
      });
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-primary to-orange-500 text-white p-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MapPin className="w-5 h-5" />
            <h2 className="font-semibold">
              {i18n.language === 'fr' ? 'Choisir ma position' : 'Choose my location'}
            </h2>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-white/20 rounded-full transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Search Bar */}
        <div className="p-4 border-b bg-gray-50">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && searchAddress()}
                placeholder={i18n.language === 'fr' ? "Rechercher une adresse..." : "Search for an address..."}
                className="pr-10"
              />
              <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            </div>
            <Button 
              onClick={searchAddress} 
              disabled={searching}
              variant="outline"
            >
              {searching ? '...' : (i18n.language === 'fr' ? 'Chercher' : 'Search')}
            </Button>
            <Button 
              onClick={getCurrentLocation}
              disabled={gettingLocation}
              variant="outline"
              className="flex items-center gap-1"
            >
              <Navigation className="w-4 h-4" />
              {gettingLocation ? '...' : (i18n.language === 'fr' ? 'Ma position' : 'My location')}
            </Button>
          </div>
          
          {address && (
            <div className="mt-2 flex items-center gap-2 text-sm text-gray-600">
              <MapPin className="w-4 h-4 text-primary" />
              <span>{address}</span>
            </div>
          )}
        </div>

        {/* Map */}
        <div className="h-96">
          <MapContainer
            center={mapCenter}
            zoom={position ? 13 : 6}
            style={{ height: '100%', width: '100%' }}
            className="z-0"
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <MapClickHandler onLocationSelect={handleMapClick} />
            <MapRecenter center={mapCenter} />
            {position && (
              <Marker position={position} icon={orangeIcon} />
            )}
          </MapContainer>
        </div>

        {/* Instructions */}
        <div className="p-3 bg-blue-50 text-blue-800 text-sm text-center">
          {i18n.language === 'fr' 
            ? '💡 Cliquez sur la carte pour placer votre position, ou utilisez la recherche / géolocalisation'
            : '💡 Click on the map to place your position, or use search / geolocation'}
        </div>

        {/* Footer */}
        <div className="p-4 border-t bg-gray-50 flex justify-end gap-3">
          <Button variant="outline" onClick={onClose}>
            {i18n.language === 'fr' ? 'Annuler' : 'Cancel'}
          </Button>
          <Button 
            onClick={handleConfirm}
            disabled={!position}
            className="bg-primary hover:bg-primary/90"
          >
            <Check className="w-4 h-4 mr-2" />
            {i18n.language === 'fr' ? 'Confirmer' : 'Confirm'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default LocationPicker;
